<template>
  <AppNavbar />
  <div class="container mt-4 cobro-shared-backlog">
    <AppHeader
      title="Cobro de Pedidos Publicados"
      subtitle="Backlog compartido de pedidos disponibles para cobrar en caja"
    >
      <template #buttons>
        <router-link class="btn btn-outline-secondary" :to="{ name: 'Caja' }">
          <i class="bi bi-arrow-left me-1"></i>Volver
        </router-link>
      </template>
    </AppHeader>

    <section class="shared-backlog-panel mt-4">
      <div class="shared-backlog-header">
        <div>
          <div class="d-flex align-items-center gap-2 mb-1">
            <i class="bi bi-inboxes fs-5 text-primary"></i>
            <h5 class="mb-0">Pendientes compartidos de cobro</h5>
          </div>
          <p class="text-muted mb-0 small">
            Estos pedidos pueden ser cobrados por cualquier caja abierta de la sucursal. El movimiento queda asignado a esta caja recién al registrar el cobro.
          </p>
        </div>
        <div class="backlog-metrics">
          <span class="metric-label">Disponibles</span>
          <strong>{{ filteredInvoices.length }}</strong>
        </div>
      </div>

      <div class="shared-backlog-toolbar">
        <div class="search-box">
          <i class="bi bi-search"></i>
          <input
            v-model="searchTerm"
            type="text"
            class="form-control"
            placeholder="Buscar comprobante o cliente"
          />
        </div>
        <button
          class="btn btn-outline-primary"
          type="button"
          :disabled="isLoadingPending"
          @click="fetchPendingCollections"
        >
          <span v-if="isLoadingPending" class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
          <i v-else class="bi bi-arrow-clockwise me-1"></i>
          Actualizar
        </button>
      </div>

      <div v-if="pendingLoadError" class="alert alert-warning mb-3" role="alert">
        <i class="bi bi-exclamation-triangle me-2"></i>{{ pendingLoadError }}
      </div>

      <div v-if="isLoadingPending && invoices.length === 0" class="loading-state">
        <div class="spinner-border text-primary" role="status" aria-label="Cargando pendientes"></div>
        <span>Cargando pendientes de cobro...</span>
      </div>

      <div v-else-if="filteredInvoices.length === 0" class="empty-state">
        <i class="bi bi-check2-circle"></i>
        <strong>No hay pedidos publicados para cobrar</strong>
        <span v-if="searchTerm">No se encontraron resultados para la búsqueda actual.</span>
        <span v-else>Cuando un pedido se complete desde Ventas, aparecerá en este backlog compartido.</span>
      </div>

      <template v-else>
        <div class="pending-card-list">
          <article v-for="inv in filteredInvoices" :key="`card-${inv.id}`" class="pending-card">
            <div class="pending-card-main">
              <div>
                <span class="status-chip">Publicado para cobro</span>
                <h6>{{ inv.number }}</h6>
                <p>{{ inv.client || 'Cliente no informado' }}</p>
              </div>
              <div class="pending-total">{{ formatCurrency(inv.total) }}</div>
            </div>
            <div v-if="inv.ultimoError" class="alert alert-warning py-1 px-2 small mb-2">
              <i class="bi bi-exclamation-triangle me-1"></i>{{ inv.ultimoError }}
            </div>
            <div class="pending-card-meta">
              <span><i class="bi bi-calendar3 me-1"></i>{{ inv.date }}</span>
              <span><i class="bi bi-shop me-1"></i>Sucursal compartida</span>
            </div>
            <div class="pending-card-actions">
              <button
                class="btn btn-outline-primary btn-sm"
                title="Ver detalle"
                @click="openDetalle(inv)"
              >
                <i class="bi bi-eye me-1"></i>Detalle
              </button>
              <button
                class="btn btn-success btn-sm"
                title="Registrar cobro en esta caja"
                @click="openPaymentModal(inv)"
              >
                <i class="bi bi-cash-coin me-1"></i>Cobrar
              </button>
              <button
                class="btn btn-outline-danger btn-sm"
                title="Cancelar factura"
                @click="cancelarFactura(inv)"
              >
                <i class="bi bi-x-circle me-1"></i>Cancelar
              </button>
            </div>
          </article>
        </div>

        <div class="table-responsive desktop-pending-table">
          <table class="table table-hover align-middle">
            <thead class="table-light">
              <tr>
                <th style="min-width: 160px;">N° de Comprobante</th>
                <th>Cliente</th>
                <th style="min-width: 160px;">Fecha</th>
                <th class="text-end" style="min-width: 120px;">Monto Total</th>
                <th class="text-center" style="min-width: 160px;">Alcance</th>
                <th class="text-end" style="min-width: 340px;">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="inv in filteredInvoices" :key="inv.id">
                <td class="fw-semibold">{{ inv.number }}</td>
                <td>
                  <div>{{ inv.client || 'Cliente no informado' }}</div>
                  <div v-if="inv.ultimoError" class="small text-warning-emphasis">
                    <i class="bi bi-exclamation-triangle me-1"></i>{{ inv.ultimoError }}
                  </div>
                </td>
                <td><small class="text-muted">{{ inv.date }}</small></td>
                <td class="text-end fw-bold">{{ formatCurrency(inv.total) }}</td>
                <td class="text-center">
                  <span class="badge bg-primary-subtle text-primary-emphasis">Compartido</span>
                </td>
                <td class="text-end">
                  <div class="d-inline-flex gap-2">
                    <button
                      class="btn btn-outline-primary btn-sm"
                      title="Ver detalle"
                      @click="openDetalle(inv)"
                    >
                      <i class="bi bi-eye me-1"></i>Ver detalle
                    </button>
                    <button
                      class="btn btn-success btn-sm"
                      title="Registrar cobro en esta caja"
                      @click="openPaymentModal(inv)"
                    >
                      <i class="bi bi-cash-coin me-1"></i>Cobrar
                    </button>
                    <button
                      class="btn btn-outline-danger btn-sm"
                      title="Cancelar factura"
                      @click="cancelarFactura(inv)"
                    >
                      <i class="bi bi-x-circle me-1"></i>Cancelar
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </section>
  </div>

  <!-- Modal de Detalle -->
  <FacturaDetalleModal
    :show="showDetalle"
    :nroFactura="detalleNroFactura"
    :movimientoId="detalleMovimientoId"
    @close="closeDetalle"
    @pedido-convertido="handlePedidoConvertido"
  />

  <!-- Modal para Confirmar Cobro (sin AppModal) -->
  <teleport to="body">
    <div
      v-if="showPaymentModal"
      class="modal fade show"
      style="display: block;"
      id="paymentModal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="paymentModalLabel"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="paymentModalLabel">Registrar cobro en esta caja</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closePaymentModal"></button>
          </div>
          <div class="modal-body">
            <SelectorComprobante v-model="comprobante" :documento="selectedInvoices[0]?.documento || ''" />
            <PaymentForms
              :total="totalToCharge"
              :types="paymentTypes"
              v-model:payments="payments"
              cash-received-mode
              enforce-total
              @update:summary="summary = $event"
              @submit-request="submitCobro"
            />
          </div>
          <div class="modal-footer">
            <span v-if="!canSubmit && !hasCredito" class="me-auto text-danger small">
              <i class="bi bi-exclamation-circle me-1"></i>{{ blockReason }}
            </span>
            <button class="btn btn-outline-secondary" @click="closePaymentModal">
              Cancelar <kbd class="ms-1 d-none d-lg-inline">Esc</kbd>
            </button>
            <button class="btn btn-success" :disabled="!canSubmit" @click="submitCobro">
              <i class="bi bi-check2-circle me-1"></i>Registrar Cobro <kbd class="ms-1 d-none d-lg-inline">F9</kbd>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="showPaymentModal"
      class="modal-backdrop fade show"
      @click="closePaymentModal"
    ></div>
  </teleport>

  <!-- COP-7A-007: seguimiento público de factura -->
  <EnviarSeguimientoModal
    :open="mostrarSeguimientoModal"
    :tracking-url="seguimientoTrackingUrl"
    :telefono-inicial="seguimientoTelefono"
    @cerrado="mostrarSeguimientoModal = false"
  />
</template>

<script setup>
import { textoMotivoCobro } from '@/utils/motivoCobro.mjs'
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import AppNavbar from '@/components/AppNavbar.vue'
import AppHeader from '@/components/AppHeader.vue'
import PaymentForms from '@/components/PaymentForms.vue'
import SelectorComprobante from '@/components/SelectorComprobante.vue'
import FacturaDetalleModal from '@/components/FacturaDetalleModal.vue'
import EnviarSeguimientoModal from '@/components/EnviarSeguimientoModal.vue'
// Nota: Modal inline via <teleport>, no usamos AppModal
import apiService from '@/services/apiService.js'
import { useCashboxStore } from '@/stores/cashbox'
import localPrintSettingsService from '@/services/localPrintSettingsService'
import invoiceLocalPrintService from '@/services/invoiceLocalPrintService'
import keyboardShortcutService from '@/services/keyboardShortcutService'


// Search term and invoices (populated from backend)
const searchTerm = ref('')
const invoices = reactive([])
const selectedInvoices = reactive([])
// P2 RN-VI2: factura electrónica por defecto; ticket sólo si el cajero lo elige.
const comprobante = ref('FACTURA')
const isLoadingPending = ref(false)
const pendingLoadError = ref('')


// Formas de pago
const payments = ref([])
const paymentTypes = ['Efectivo', 'Tarjeta', 'Cheque', 'Transferencia', 'Credito']

// Filtrado de facturas
const filteredInvoices = computed(() =>
  invoices.filter(inv => {
    const term = (searchTerm.value || '').toLowerCase()
    return (inv.number || '').toLowerCase().includes(term) || (inv.client || '').toLowerCase().includes(term)
  })
)



// Cálculo de totales
const totalToCharge = computed(() =>
  selectedInvoices.reduce((sum, inv) => sum + inv.total, 0)
)






const canAddPayment = computed(() =>
  payments.value.length > 0
)

// Verificar si se seleccionó Crédito
const hasCredito = computed(() =>
  payments.value.some(p => (p.type || '').toUpperCase() === 'CREDITO')
)

// Validación de suficiencia en el frontend, espejo del backend
// (SPEC_COBRO_PERFORMANCE_v0.1.md, RF-7). El crédito no aplica.
const summary = ref(null)

const canSubmit = computed(() => {
  if (!canAddPayment.value) return false
  if (hasCredito.value) return true
  return Boolean(summary.value?.valid)
})

const blockReason = computed(() => {
  if (!canAddPayment.value) return 'Cargá con cuánto paga el cliente.'
  if (summary.value?.invalidChangeMethods?.length) {
    return `No se puede dar vuelto en ${summary.value.invalidChangeMethods.join(' / ').toLowerCase()}.`
  }
  if (!summary.value?.sufficient) {
    const falta = new Intl.NumberFormat('es-PY').format(Math.round(Number(summary.value?.remaining) || 0))
    return `Faltan ₲ ${falta} para cubrir el total.`
  }
  return ''
})

const cashboxStore = useCashboxStore()

function resolveApiMessage(err, fallback) {
  return err?.response?.data?.message
    || err?.response?.data?.details?.error
    || err?.response?.data?.error
    || err?.message
    || fallback
}

// COP-7A-007: seguimiento del link de factura post-cobro. El contacto lo
// resuelve el backend (pedido > ficha del cliente > venta) y viaja en la
// respuesta del cobro — acá no hay formulario de cliente del que sacarlo.
const mostrarSeguimientoModal = ref(false)
const seguimientoTrackingUrl = ref('')
const seguimientoTelefono = ref('')

// Modal de pago
const showPaymentModal = ref(false)
function openPaymentModal(inv) {
  selectedInvoices.splice(0, selectedInvoices.length, inv)
  summary.value = null
  comprobante.value = 'FACTURA'
  // Pre-cargar efectivo por el monto total de la factura
  payments.value = [{ type: 'Efectivo', amount: Number(inv.total || 0) }]
  showPaymentModal.value = true
}
function closePaymentModal() {
  showPaymentModal.value = false
}

// Atajos del modal de cobro: F9/Ctrl+Enter registra, Escape cierra sin cobrar.
// El scope se registra solo mientras el modal está abierto para no interferir
// con ningún otro atajo de la pantalla.
let unregisterPaymentModalShortcuts = null
watch(showPaymentModal, (isOpen) => {
  if (isOpen) {
    unregisterPaymentModalShortcuts = keyboardShortcutService.register({
      scopeId: 'cobro-factura-venta-modal',
      bindings: [
        { key: 'F9', run: submitCobro, enabled: () => canSubmit.value, label: 'Registrar cobro' },
        { key: 'Enter', ctrlKey: true, run: submitCobro, enabled: () => canSubmit.value, label: 'Registrar cobro' },
        { key: 'Escape', run: closePaymentModal, label: 'Cerrar' }
      ]
    })
  } else if (unregisterPaymentModalShortcuts) {
    unregisterPaymentModalShortcuts()
    unregisterPaymentModalShortcuts = null
  }
})

onBeforeUnmount(() => {
  if (unregisterPaymentModalShortcuts) {
    unregisterPaymentModalShortcuts()
    unregisterPaymentModalShortcuts = null
  }
})

async function submitCobro() {
  if (!canSubmit.value) return
  if (selectedInvoices.length === 0) {
    alert('Selecciona una factura para cobrar.');
    return;
  }

  // Determinar forma de operación según si hay Crédito seleccionado
  const formaOperacion = hasCredito.value ? 'CREDITO' : 'CONTADO'

  // Usamos la primera factura seleccionada (UI restringe a 1)
  const movimientoId = selectedInvoices[0].id

  // Si es crédito, enviar pagos vacío; si no, mapear pagos normalmente
  const pagos = hasCredito.value ? [] : (payments.value || []).map(p => {
    // soportar varias formas de nombrar campos según el componente
    const metodo = (p.metodo_pago || p.metodo || p.type || p.name || '').toString().toUpperCase()
    const monto = Number(p.monto ?? p.amount ?? p.value ?? 0)
    const referencia = p.referencia_externa ?? p.referencia ?? p.reference ?? p.ref ?? null
    return {
      metodo_pago: metodo,
      monto: monto,
      referencia_externa: referencia === undefined ? null : referencia
    }
  })

  const payload = {
    movimiento_id: movimientoId,
    forma_operacion: formaOperacion,
    pagos: pagos,
    comprobante: comprobante.value
  }

  // Loguear payload antes de enviar (como pidió)
  console.log('Payload -> /api/cashbox/collect-sale', payload)

  try {
    const resp = await apiService.post('/api/cashbox/collect-sale', payload)
    console.log('Respuesta collect-sale:', resp && resp.data)
    alert('Cobro registrado correctamente.')
    const responseData = resp?.data?.data || null
    const saleId = Number(responseData?.id_venta || 0)
    if (saleId > 0) {
      await maybePrintInvoice({
        saleId,
        movimientoId,
        invoiceNumber: responseData?.nro_factura || selectedInvoices[0]?.number || '',
        fiscal: responseData?.fiscal || null
      })
    }

    // limpiar estado: quitar factura seleccionada y pagos
    selectedInvoices.splice(0, selectedInvoices.length)
    payments.value = []
    closePaymentModal()

    // COP-7A-007: en vez de dejar el modal de cobro abierto sin hacer nada,
    // si el backend armó un link de seguimiento (factura emitida o en
    // contingencia, requiere PUBLIC_TRACKING_BASE_URL en el entorno) se
    // ofrece mandárselo al cliente.
    const trackingUrl = responseData?.tracking_url || null
    if (trackingUrl && saleId > 0) {
      seguimientoTrackingUrl.value = trackingUrl
      seguimientoTelefono.value = responseData?.tracking_contacto?.telefono || ''
      mostrarSeguimientoModal.value = true
    }

    // Refresh: actualizar lista de pendientes y estado global de caja
    await fetchPendingCollections()
    if (cashboxStore && typeof cashboxStore.fetchCurrentOpen === 'function') {
      await cashboxStore.fetchCurrentOpen()
    }
  } catch (err) {
    console.error('Error enviando collect-sale:', err)
    alert(resolveApiMessage(err, 'Error al registrar el cobro.'))
  }
}

async function maybePrintInvoice({ saleId, movimientoId, invoiceNumber = '', fiscal = null }) {
  const settings = localPrintSettingsService.getSettings()
  // P2 RN-VI5: cobrada con ticket, no hay factura.
  const sinFactura = fiscal?.comprobante === 'TICKET'
  if (!settings.enabled || settings.invoicePrintMode === 'disabled') return

  if (settings.invoicePrintMode === 'confirm') {
    const confirmed = await window.confirm({
      title: sinFactura ? 'Imprimir ticket' : 'Imprimir factura',
      message: `${sinFactura ? 'El ticket' : 'La factura'} ${invoiceNumber || saleId} ${sinFactura ? 'está listo' : 'está lista'}. ¿Deseas enviarl${sinFactura ? 'o' : 'a'} ahora a la impresora local?`,
      confirmText: 'Imprimir',
      cancelText: 'Omitir'
    })
    if (!confirmed) return
  }

  try {
    const result = await invoiceLocalPrintService.printInvoiceBySale({
      saleId,
      movimientoId,
      settings,
      sinFactura
    })
    if (sinFactura) {
      alert.success('Ticket enviado al agente local. La venta queda pendiente de factura.')
      return
    }
    if (result?.mode === 'offline_contingency' || fiscal?.status === 'OFFLINE_PENDING_SYNC') {
      alert.success('Comprobante temporal enviado al agente local. Queda pendiente de validación fiscal.')
      return
    }
    alert.success('Factura enviada al cliente local de impresión.')
  } catch (error) {
    console.error('No se pudo imprimir la factura localmente', error)
    alert.error(error?.message || 'No se pudo imprimir la factura en el cliente local.')
  }
}

// Fetch pending collections from backend and populate invoices
async function fetchPendingCollections() {
  isLoadingPending.value = true
  pendingLoadError.value = ''
  try {
    const res = await apiService.get('/api/cashbox/pending-collections')
    const payload = res.data
    if (payload && payload.success && Array.isArray(payload.data)) {
      const mapped = payload.data.map(item => ({
        id: item.movimiento_id,
        number: item.nro_comprobante_origen || `MOV-${item.movimiento_id}`, // este es el nro_factura que usaremos para ver detalle
        client: item.nombre_razon_social,
        // P2 RN-VI1: decide si se ofrece el ticket.
        documento: item.nro_documento || '',
        date: new Date(item.fecha_movimiento).toLocaleString(),
        total: Number(item.monto_total),
        // RN-CF2: por qué falló el último intento de cobro, si falló.
        ultimoError: textoMotivoCobro(item.ultimo_error_cobro)
      }))
      // replace contents of reactive array
      invoices.splice(0, invoices.length, ...mapped)
    } else {
      console.warn('Unexpected response from pending-collections', payload)
      pendingLoadError.value = 'La respuesta de pendientes no tiene el formato esperado.'
    }
  } catch (err) {
    console.error('Error fetching pending collections:', err)
    pendingLoadError.value = resolveApiMessage(err, 'No se pudieron cargar los pedidos pendientes de cobro.')
    if (invoices.length === 0) {
      alert(pendingLoadError.value)
    }
  } finally {
    isLoadingPending.value = false
  }
}

onMounted(() => { fetchPendingCollections() })

// Formateo de moneda
function formatCurrency(value) {
  return new Intl.NumberFormat('es-PY', {
    style: 'currency',
    currency: 'PYG'
  }).format(value)
}

// ---------------- Ver detalle (modal) -----------------
const showDetalle = ref(false)
const detalleNroFactura = ref(null)
const detalleMovimientoId = ref(null)
function openDetalle(inv) {
  // inv.number contiene el nro_comprobante_origen -> nro_factura
  detalleNroFactura.value = inv?.number ?? null
  detalleMovimientoId.value = inv?.id ?? null
  if (detalleNroFactura.value) showDetalle.value = true
}
function closeDetalle() {
  showDetalle.value = false
}

// Handler para cuando se convierte a pedido
async function handlePedidoConvertido() {
  // Recargar la lista de facturas pendientes
  await fetchPendingCollections()
  // Actualizar el estado de la caja si es necesario
  if (cashboxStore && typeof cashboxStore.fetchCurrentOpen === 'function') {
    await cashboxStore.fetchCurrentOpen()
  }
}

// Función para cancelar factura
async function cancelarFactura(inv) {
  const confirmacion = await confirm({
    message: `¿Está seguro que desea CANCELAR la factura ${inv.number}?\n\nEsta acción no se puede deshacer.`,
    title: 'Cancelar factura',
    confirmText: 'Cancelar factura',
    cancelText: 'Mantener'
  })
  
  if (!confirmacion) return

  try {
    const payload = {
      movimiento_id: inv.id,
      accion: 'CANCELAR'
    }

    console.log('Payload -> /api/cashbox/collect-sale (Cancelar):', payload)

    const response = await apiService.post('/api/cashbox/collect-sale', payload)
    
    console.log('Respuesta cancelar factura:', response?.data)

    if (response?.data?.ok) {
      alert(`Factura ${inv.number} cancelada exitosamente.`)
      // Recargar la lista de facturas pendientes
      await fetchPendingCollections()
      // Actualizar el estado de la caja
      if (cashboxStore && typeof cashboxStore.fetchCurrentOpen === 'function') {
        await cashboxStore.fetchCurrentOpen()
      }
    } else {
      alert('Error: No se pudo cancelar la factura.')
    }
  } catch (err) {
    console.error('Error al cancelar factura:', err)
    alert('Error al cancelar la factura. Revisa la consola para más detalles.')
  }
}
</script>
<style scoped>
.cobro-shared-backlog {
  padding-bottom: 2rem;
}

.shared-backlog-panel {
  background: #fff;
  border: 1px solid #e6e9ee;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.shared-backlog-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
}

.backlog-metrics {
  min-width: 112px;
  border: 1px solid #d9e2ef;
  border-radius: 8px;
  padding: .55rem .75rem;
  text-align: center;
  background: #f8fafc;
}

.metric-label {
  display: block;
  color: #64748b;
  font-size: .78rem;
  line-height: 1;
}

.backlog-metrics strong {
  display: block;
  color: #0f172a;
  font-size: 1.45rem;
  line-height: 1.15;
}

.shared-backlog-toolbar {
  display: flex;
  align-items: center;
  gap: .75rem;
  margin-bottom: 1rem;
}

.search-box {
  position: relative;
  flex: 1;
}

.search-box i {
  position: absolute;
  top: 50%;
  left: .75rem;
  transform: translateY(-50%);
  color: #64748b;
  pointer-events: none;
}

.search-box .form-control {
  padding-left: 2.25rem;
}

.loading-state,
.empty-state {
  min-height: 180px;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: .5rem;
  color: #64748b;
  text-align: center;
  padding: 1rem;
}

.empty-state i {
  font-size: 2rem;
  color: #198754;
}

.empty-state strong {
  color: #0f172a;
}

.pending-card-list {
  display: none;
}

.pending-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: .9rem;
  background: #fff;
}

.pending-card-main {
  display: flex;
  justify-content: space-between;
  gap: .75rem;
}

.pending-card h6 {
  margin: .35rem 0 .15rem;
  font-weight: 700;
}

.pending-card p {
  margin: 0;
  color: #475569;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: .18rem .55rem;
  background: #e7f1ff;
  color: #084298;
  font-size: .75rem;
  font-weight: 600;
}

.pending-total {
  color: #0f5132;
  font-weight: 700;
  white-space: nowrap;
}

.pending-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: .45rem .9rem;
  margin-top: .8rem;
  color: #64748b;
  font-size: .86rem;
}

.pending-card-actions {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: .5rem;
  margin-top: .9rem;
}

.table td, .table th {
  vertical-align: middle;
}

.table-responsive {
  overflow-x: auto;
}

@media (max-width: 991.98px) {
  .shared-backlog-header,
  .shared-backlog-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .backlog-metrics {
    text-align: left;
  }

  .desktop-pending-table {
    display: none;
  }

  .pending-card-list {
    display: grid;
    gap: .75rem;
  }

  .pending-card-main {
    flex-direction: column;
  }

  .pending-card-actions {
    grid-template-columns: 1fr;
  }
}
</style>
